import { ArrowDownTrayIcon, ArrowUpTrayIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  commitPaymentImport,
  deletePaymentImportTemplate,
  downloadPaymentImportSample,
  parsePaymentImportFile,
  previewPaymentImport,
  revertPaymentImport,
  savePaymentImportTemplate,
} from 'services'
import { Button, Checkbox, Input, Select } from 'stories/components'
import { formatDate, getPrice2decimal } from 'utils'

import type {
  PaymentImportBatch,
  PaymentImportField,
  PaymentImportOptions,
  PaymentImportRow,
  PaymentImportSummary,
  PaymentImportTemplate,
} from './types'

type Step = 'upload' | 'map' | 'preview' | 'done'

export const isPaymentHistoryFile = (name: string) => /\.(csv|xls|xlsx|xlsm|txt)$/i.test(name || '')

const money = (v: any) => (v === null || v === undefined || v === '' ? '-' : `$${getPrice2decimal(v)}`)

const statusPill: Record<string, string> = {
  ready: 'bg-blue-100 text-blue-700',
  applied: 'bg-green-100 text-green-700',
  skipped: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
  reverted: 'bg-gray-100 text-gray-600',
}

const statusLabel: Record<string, string> = {
  ready: 'Will import',
  applied: 'Imported',
  skipped: 'Skipped',
  error: 'Error',
  failed: 'Failed',
  reverted: 'Reverted',
}

/** How a payment that is not exactly the amount due is handled when the file has no split columns. */
const partialOptions = {
  suspense: 'Credit to suspense as Other Cash (schedule does not advance)',
  waterfall: 'Apply in waterfall order: interest, principal, escrow, charges',
}

const overpaymentOptions = {
  suspense: 'Credit the excess to suspense',
  principal: 'Apply the excess to principal',
}

const dateFormatOptions = {
  auto: 'Detect automatically',
  MDY: 'Month first (12/31/2026)',
  DMY: 'Day first (31/12/2026)',
  YMD: 'Year first (2026-12-31)',
}

interface Props {
  /** Set when the import runs inside one loan — the file then needs no Loan Number column. */
  loanNumber?: number
  /** A file dropped on the loan's Payments screen. Parsed as soon as the wizard opens. */
  initialFile?: File | null
  /** Called after payments were posted, so the host screen can reload its grid. */
  onImported?: () => void
}

/**
 * Upload a payment file, map its columns onto the payment fields, review every row, then post.
 * Used both as the portfolio-wide Payment Import screen and, scoped to one loan, from that loan's
 * Loan Payments screen.
 */
export const PaymentImportWizard = ({ loanNumber, initialFile, onImported }: Props) => {
  const [step, setStep] = useState<Step>('upload')
  const [isLoading, setLoading] = useState(false)
  const [hasHeaderRow, setHasHeaderRow] = useState(true)
  const [batch, setBatch] = useState<PaymentImportBatch | null>(null)
  const [fields, setFields] = useState<PaymentImportField[]>([])
  const [types, setTypes] = useState<Record<string, string>>({})
  const [templates, setTemplates] = useState<PaymentImportTemplate[]>([])
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [options, setOptions] = useState<PaymentImportOptions>({})
  const [rows, setRows] = useState<PaymentImportRow[]>([])
  const [summary, setSummary] = useState<PaymentImportSummary | null>(null)
  const [templateName, setTemplateName] = useState('')
  // null until the user answers. Export stays disabled until then.
  const [omitFromReport, setOmitFromReport] = useState<boolean | null>(null)
  const parsedFile = useRef<File | null>(null)

  const columnOptions = useMemo(() => {
    const rlt: Record<string, string> = {}
    ;(batch?.headers || []).forEach((header, index) => {
      const sample = (batch?.sampleRows || []).map((row) => row[index]).find((v) => `${v ?? ''}`.trim() !== '')
      rlt[`${index}`] = sample ? `${header}  —  e.g. ${sample}` : header
    })
    return rlt
  }, [batch])

  const groups = useMemo(() => {
    const rlt: { name: string; fields: PaymentImportField[] }[] = []
    fields.forEach((field) => {
      if (field.key === 'loanNumber' && loanNumber) return
      const group = rlt.find((g) => g.name === field.group)
      if (group) group.fields.push(field)
      else rlt.push({ name: field.group, fields: [field] })
    })
    return rlt
  }, [fields, loanNumber])

  const missingRequired = useMemo(() => {
    return fields
      .filter((f) => f.required && !(f.key === 'loanNumber' && loanNumber))
      .filter((f) => mapping[f.key] === undefined || mapping[f.key] === null)
      .map((f) => f.title)
  }, [fields, mapping, loanNumber])

  const unmappedColumns = useMemo(() => {
    const used = new Set(Object.values(mapping).map((v) => Number(v)))
    return (batch?.headers || []).filter((_, index) => !used.has(index))
  }, [batch, mapping])

  const reset = () => {
    setBatch(null)
    setRows([])
    setSummary(null)
    setMapping({})
    setOmitFromReport(null)
    setStep('upload')
  }

  const requiredGaps = (nextFields: PaymentImportField[], nextMapping: Record<string, number>) =>
    nextFields
      .filter((field) => field.required && !(field.key === 'loanNumber' && loanNumber))
      .filter((field) => nextMapping[field.key] === undefined || nextMapping[field.key] === null)
      .map((field) => field.title)

  const loadFile = async (file: File) => {
    setLoading(true)
    setOmitFromReport(null)
    try {
      const res: any = await parsePaymentImportFile(file, { loanNumber, hasHeaderRow })
      const nextFields: PaymentImportField[] = res.fields || []
      const nextMapping: Record<string, number> = res.mapping || {}
      const nextOptions: PaymentImportOptions = { ...(res.options || {}), loanNumber: loanNumber || null }
      setBatch(res)
      setFields(nextFields)
      setTypes(res.types || {})
      setTemplates(res.templates || [])
      setMapping(nextMapping)
      setOptions(nextOptions)
      if (res.appliedTemplate?.name) toast.info(`Applied the saved mapping "${res.appliedTemplate.name}".`)
      // Date Received and Amount Paid are the required columns. When both are already mapped,
      // go straight to the rows that will be posted.
      if (requiredGaps(nextFields, nextMapping).length) {
        setStep('map')
        return
      }
      try {
        const preview: any = await previewPaymentImport(res.id, nextMapping, nextOptions)
        setRows(preview.rows || [])
        setSummary(preview.summary || null)
        setStep('preview')
      } catch (error: any) {
        setStep('map')
        toast.error(error?.message || 'The file could not be previewed.')
      }
    } catch (error: any) {
      toast.error(error?.message || 'That file could not be read.')
    } finally {
      setLoading(false)
    }
  }

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    await loadFile(file)
  }

  useEffect(() => {
    if (!initialFile || parsedFile.current === initialFile) return
    parsedFile.current = initialFile
    void loadFile(initialFile)
  }, [initialFile])

  const setField = (key: string, value: string) => {
    setMapping((current) => {
      const next = { ...current }
      if (value === '') delete next[key]
      else {
        const index = Number(value)
        // One file column feeds one payment field — claiming it releases whoever held it.
        Object.keys(next).forEach((other) => {
          if (other !== key && Number(next[other]) === index) delete next[other]
        })
        next[key] = index
      }
      return next
    })
  }

  const setOption = (key: keyof PaymentImportOptions, value: any) => setOptions((c) => ({ ...c, [key]: value }))

  const onPreview = async () => {
    if (!batch) return
    if (missingRequired.length) {
      toast.error(`Map ${missingRequired.join(', ')} before continuing.`)
      return
    }
    setLoading(true)
    try {
      const res: any = await previewPaymentImport(batch.id, mapping, options)
      setRows(res.rows || [])
      setSummary(res.summary || null)
      setStep('preview')
    } catch (error: any) {
      toast.error(error?.message || 'The file could not be previewed.')
    } finally {
      setLoading(false)
    }
  }

  const onCommit = async () => {
    if (!batch || !summary) return
    if (omitFromReport === null) {
      toast.error('Choose Yes or No for Omit From 1098 Reporting.')
      return
    }
    if (!summary.ready) {
      toast.error('No rows are ready to import.')
      return
    }
    if (!window.confirm(`Post ${summary.ready} payment${summary.ready > 1 ? 's' : ''} totalling ${money(summary.amount)}?`))
      return
    setLoading(true)
    try {
      const res: any = await commitPaymentImport(batch.id, mapping, { ...options, omitFromReport })
      setRows(res.rows || [])
      setSummary(res.summary || null)
      setBatch((current) => (current ? { ...current, ...res, rows: undefined } : current))
      setStep('done')
      toast.success(`Imported ${res.summary?.applied ?? 0} payment(s).`)
      onImported && onImported()
    } catch (error: any) {
      toast.error(error?.message || 'The import could not be completed.')
    } finally {
      setLoading(false)
    }
  }

  const onRevert = async () => {
    if (!batch) return
    if (!window.confirm('Delete every payment this import created? The loans return to their prior state.')) return
    setLoading(true)
    try {
      const res: any = await revertPaymentImport(batch.id)
      toast.success(`Reverted ${res?.reverted ?? 0} payment(s).`)
      setRows((current) => current.map((row) => (row.status === 'applied' ? { ...row, status: 'reverted' } : row)))
      setBatch((current) => (current ? { ...current, status: 'reverted' } : current))
      onImported && onImported()
    } catch (error: any) {
      toast.error(error?.message || 'The import could not be reverted.')
    } finally {
      setLoading(false)
    }
  }

  const onSaveTemplate = async () => {
    if (!batch) return
    const name = templateName.trim()
    if (!name) {
      toast.error('Name the mapping first.')
      return
    }
    setLoading(true)
    try {
      await savePaymentImportTemplate({ name, headers: batch.headers, mapping, options })
      const existing = templates.find((t) => t.name === name)
      const saved = { id: existing?.id ?? 0, name, headers: batch.headers, mapping, options }
      setTemplates((current) => [...current.filter((t) => t.name !== name), saved])
      setTemplateName('')
      toast.success(`Saved the mapping "${name}".`)
    } catch (error: any) {
      toast.error(error?.message || 'The mapping could not be saved.')
    } finally {
      setLoading(false)
    }
  }

  const onApplyTemplate = (template: PaymentImportTemplate) => {
    setMapping(template.mapping || {})
    setOptions({ ...(template.options || {}), loanNumber: loanNumber || null })
    toast.info(`Applied "${template.name}".`)
  }

  const onDeleteTemplate = async (template: PaymentImportTemplate) => {
    if (!template.id) return
    setLoading(true)
    try {
      await deletePaymentImportTemplate(template.id)
      setTemplates((current) => current.filter((t) => t.id !== template.id))
    } catch (error: any) {
      toast.error(error?.message || 'The mapping could not be deleted.')
    } finally {
      setLoading(false)
    }
  }

  const loanCell = (row: PaymentImportRow) =>
    row.loanNumber ? (
      <Link to={`/servicing/${row.loanNumber}`} className="text-shade-blue hover:underline font-semibold">
        {row.loanNumber}
      </Link>
    ) : (
      <span className="text-gray-500">{row.loanRef || '-'}</span>
    )

  const rowTable = (
    <div className="overflow-x-auto border border-gray-100 rounded">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200 bg-slate-50">
            <th className="p-3 font-semibold whitespace-nowrap">Row</th>
            <th className="p-3 font-semibold whitespace-nowrap">Loan #</th>
            <th className="p-3 font-semibold whitespace-nowrap">Borrower</th>
            <th className="p-3 font-semibold whitespace-nowrap">Due</th>
            <th className="p-3 font-semibold whitespace-nowrap">Received</th>
            <th className="p-3 font-semibold whitespace-nowrap">Deposited</th>
            <th className="p-3 font-semibold whitespace-nowrap text-right">Amount</th>
            <th className="p-3 font-semibold whitespace-nowrap text-right">Principal</th>
            <th className="p-3 font-semibold whitespace-nowrap text-right">Interest</th>
            <th className="p-3 font-semibold whitespace-nowrap">Type</th>
            <th className="p-3 font-semibold whitespace-nowrap">Status</th>
            <th className="p-3 font-semibold">Notes</th>
          </tr>
        </thead>
        <tbody className="text-gray-900">
          {rows.map((row) => (
            <tr key={row.row} className="border-b border-gray-100 hover:bg-slate-50 align-top">
              <td className="p-3 whitespace-nowrap text-gray-500">{row.row}</td>
              <td className="p-3 whitespace-nowrap">{loanCell(row)}</td>
              <td className="p-3 whitespace-nowrap">{row.borrowerName || '-'}</td>
              <td className="p-3 whitespace-nowrap">
                {row.values?.paymentDueDate ? formatDate(row.values.paymentDueDate) : '-'}
              </td>
              <td className="p-3 whitespace-nowrap">
                {row.values?.paymentReceivedDate ? formatDate(row.values.paymentReceivedDate) : '-'}
              </td>
              <td className="p-3 whitespace-nowrap">
                {row.values?.paymentDepositedDate ? formatDate(row.values.paymentDepositedDate) : '-'}
              </td>
              <td className="p-3 whitespace-nowrap text-right font-semibold">{money(row.values?.amountPaid)}</td>
              <td className="p-3 whitespace-nowrap text-right">
                {money(row.values?.principal)}
                {row.allocationSource === 'auto' ? <span className="block text-xs text-gray-400">from schedule</span> : null}
              </td>
              <td className="p-3 whitespace-nowrap text-right">{money(row.values?.interest)}</td>
              <td className="p-3 whitespace-nowrap">{types[row.values?.type] || row.values?.type || '-'}</td>
              <td className="p-3 whitespace-nowrap">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusPill[row.status] || ''}`}>
                  {statusLabel[row.status] || row.status}
                </span>
              </td>
              <td className="p-3 text-xs text-gray-600">
                {row.messages?.length ? row.messages.join(' ') : ''}
                {row.loanPaymentId ? <span className="text-gray-400"> (payment #{row.loanPaymentId})</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  const summaryTiles = summary && (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
      {[
        { label: 'Rows', value: summary.total, tone: 'text-gray-900' },
        { label: step === 'done' ? 'Imported' : 'Will import', value: step === 'done' ? summary.applied : summary.ready, tone: 'text-green-700' },
        { label: 'Skipped', value: summary.skipped, tone: 'text-yellow-700' },
        {
          label: step === 'done' ? 'Not imported' : 'Errors',
          value: step === 'done' ? summary.failed + summary.errors : summary.errors,
          tone: 'text-red-700',
        },
        { label: 'Total amount', value: money(summary.amount), tone: 'text-gray-900' },
      ].map((tile) => (
        <div key={tile.label} className="border border-gray-100 rounded p-3 bg-slate-50">
          <div className="text-xs text-gray-500">{tile.label}</div>
          <div className={`text-lg font-bold ${tile.tone}`}>{tile.value}</div>
        </div>
      ))}
    </div>
  )

  return (
    <div className="payment-import-wizard relative">
      <LayoutLoading show={isLoading} />

      {step === 'upload' && (
        <div className="max-w-3xl">
          <div className="border border-dashed border-gray-300 rounded p-8 text-center bg-slate-50">
            <ArrowUpTrayIcon className="w-8 h-8 mx-auto text-gray-400" />
            <p className="mt-2 text-gray-700 font-semibold">Upload a payment file</p>
            <p className="text-sm text-gray-500 mt-1">
              CSV, XLS or XLSX. You choose which column is which on the next step
              {loanNumber ? `, and every row posts to loan #${loanNumber}.` : ', including the loan number.'}
            </p>
            <label className="inline-block mt-4">
              <span className="bg-shade-blue text-white rounded px-4 py-2 text-sm font-semibold cursor-pointer inline-block">
                Choose file
              </span>
              <input className="hidden" type="file" accept=".csv,.xls,.xlsx,.xlsm,.txt" onChange={onFile} />
            </label>
            <div className="mt-4 flex items-center justify-center gap-6">
              <div className="w-64">
                <Checkbox
                  id="paymentImportHasHeader"
                  title="First row has column headers"
                  value={hasHeaderRow}
                  onChange={(value: boolean) => setHasHeaderRow(value)}
                />
              </div>
              <span
                className="text-shade-blue cursor-pointer flex items-center gap-1 hover:underline text-sm"
                onClick={() => downloadPaymentImportSample()}
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Template CSV
              </span>
            </div>
          </div>
        </div>
      )}

      {step === 'map' && batch && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-semibold text-gray-900">{batch.fileName}</p>
              <p className="text-sm text-gray-500">
                {batch.rowCount} row{batch.rowCount === 1 ? '' : 's'}
                {batch.sheetName ? ` · sheet "${batch.sheetName}"` : ''} · {batch.headers.length} columns
              </p>
            </div>
            <Button onClick={reset} color="gray" className="!py-2">
              Choose another file
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <h3 className="font-bold text-gray-900 mb-2">Map the columns</h3>
              {missingRequired.length > 0 && (
                <p className="text-sm text-red-700 mb-3">Still to map: {missingRequired.join(', ')}.</p>
              )}
              {groups.map((group) => (
                <div key={group.name} className="mb-5">
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">{group.name}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    {group.fields.map((field) => (
                      <div key={field.key}>
                        <Select
                          id={`map-${field.key}`}
                          title={field.title}
                          required={field.required}
                          hasDefaultOption
                          defaultOptionText="- Not mapped -"
                          options={columnOptions}
                          value={mapping[field.key] === undefined ? '' : `${mapping[field.key]}`}
                          onChange={(value: string) => setField(field.key, value)}
                        />
                        {field.help && <p className="text-xs text-gray-500 -mt-3 mb-3">{field.help}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {unmappedColumns.length > 0 && (
                <p className="text-xs text-gray-500">Columns left out: {unmappedColumns.join(', ')}.</p>
              )}
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">Options</h3>
              <Select
                id="allocation"
                title="Payment split"
                options={{
                  file: 'Use the split columns from the file',
                  auto: "Always split off the loan's schedule",
                }}
                value={options.allocation || 'file'}
                onChange={(value: string) => setOption('allocation', value)}
              />
              <Select
                id="overpayment"
                title="More than the amount due"
                options={overpaymentOptions}
                value={options.overpayment || 'suspense'}
                onChange={(value: string) => setOption('overpayment', value)}
              />
              <Select
                id="partial"
                title="Less than the amount due"
                options={partialOptions}
                value={options.partial || 'suspense'}
                onChange={(value: string) => setOption('partial', value)}
              />
              <Select
                id="duplicates"
                title="Rows already on the loan"
                options={{ skip: 'Skip them', import: 'Import them anyway' }}
                value={options.duplicates || 'skip'}
                onChange={(value: string) => setOption('duplicates', value)}
              />
              <Select
                id="dateFormat"
                title="Date format"
                options={dateFormatOptions}
                value={options.dateFormat || 'auto'}
                onChange={(value: string) => setOption('dateFormat', value)}
              />
              <Select
                id="defaultType"
                title="Payment type when the file has none"
                options={types}
                value={options.defaultType || 'payment'}
                onChange={(value: string) => setOption('defaultType', value)}
              />
              <Input
                name="defaultCheckACH"
                title="Default Check / ACH #"
                placeholder="Used when the file has no reference"
                value={options.defaultCheckACH || ''}
                onChange={(value: string) => setOption('defaultCheckACH', value)}
              />
              <Input
                name="defaultPaymentCode"
                title="Default payment code"
                placeholder="e.g. LOCKBOX"
                value={options.defaultPaymentCode || ''}
                onChange={(value: string) => setOption('defaultPaymentCode', value)}
              />
              <Checkbox
                id="skipOnHold"
                title="Skip loans on Hold instead of failing them"
                value={options.skipOnHold !== false}
                onChange={(value: boolean) => setOption('skipOnHold', value)}
              />

              <h3 className="font-bold text-gray-900 mt-6 mb-2">Saved mappings</h3>
              {templates.length === 0 && <p className="text-sm text-gray-500 mb-2">None saved yet.</p>}
              {templates.map((template) => (
                <div key={`${template.id}-${template.name}`} className="flex items-center justify-between text-sm mb-1">
                  <span
                    className="text-shade-blue cursor-pointer hover:underline"
                    onClick={() => onApplyTemplate(template)}
                  >
                    {template.name}
                  </span>
                  <TrashIcon
                    className="w-4 h-4 text-gray-400 cursor-pointer hover:text-red-600"
                    onClick={() => onDeleteTemplate(template)}
                  />
                </div>
              ))}
              <div className="flex items-end gap-2 mt-2">
                <div className="flex-1">
                  <Input
                    name="templateName"
                    title="Save this mapping as"
                    placeholder="e.g. Lockbox monthly"
                    value={templateName}
                    onChange={(value: string) => setTemplateName(value)}
                  />
                </div>
                <Button onClick={onSaveTemplate} color="gray" className="!py-2 mb-4">
                  Save
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <Button onClick={onPreview} disabled={isLoading} className="!py-2">
              Preview rows
            </Button>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div>
          {summaryTiles}
          {rowTable}
          <div className="mt-4 border rounded p-4">
            <p className="font-semibold">Omit From 1098 Reporting?</p>
            <p className="text-sm text-gray-500 mt-1">
              Yes checks that box on every payment this export creates. A column mapped to the field still wins for
              that row.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                color={omitFromReport === true ? 'shade-blue' : 'gray'}
                onClick={() => setOmitFromReport(true)}
                className="!py-2"
              >
                Yes
              </Button>
              <Button
                color={omitFromReport === false ? 'shade-blue' : 'gray'}
                onClick={() => setOmitFromReport(false)}
                className="!py-2"
              >
                No
              </Button>
            </div>
          </div>
          <div className="flex justify-between gap-3 mt-4">
            <Button onClick={() => setStep('map')} color="gray" className="!py-2">
              Back to mapping
            </Button>
            <Button onClick={onCommit} disabled={isLoading || !summary?.ready || omitFromReport === null} className="!py-2">
              {`Export ${summary?.ready ?? 0} payment${summary?.ready === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div>
          {summaryTiles}
          {rowTable}
          <div className="flex justify-between gap-3 mt-4">
            <Button onClick={reset} color="gray" className="!py-2">
              Import another file
            </Button>
            {batch?.status !== 'reverted' && (
              <Button onClick={onRevert} color="red" disabled={isLoading} className="!py-2">
                Revert this import
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
